/**
 * The ONE place landing-page is allowed to read `import.meta.env.VITE_*`.
 *
 * `VITE_API_URL` is the only bootstrap env var. Everything else
 * (OAuth client IDs, Stripe publishable key, branding, feature flags,
 * etc.) comes from `GET /api/config/public` via `@rynora/contracts`'s
 * `loadPublicConfig` helper.
 *
 * Other code in landing-page must NEVER touch `import.meta.env`.
 * ESLint will eventually flag any new such reads — add a getter here
 * instead.
 */

import {
  loadPublicConfig as loadPublicConfigShared,
  getCachedPublicConfig as getCachedPublicConfigShared,
  type PublicConfig,
} from '@rynora/contracts';

// Last-ditch fallbacks for SSR / Vitest / CLI contexts where neither a
// build-time `VITE_*` value nor `window.location` is available. In real
// browsers the same-origin branch below is used, so a single image works
// on any host (qa.rynora.app, uat.rynora.app, rynora.app, preview hosts)
// without per-env rebuilds — Caddy already routes /api on every public
// hostname.
const FALLBACK_API_BASE = 'http://localhost:3001';
const FALLBACK_FLOW_BUILDER_URL = 'http://localhost:3100';
const FALLBACK_STATUS_URL = 'https://status.rynora.app';

/**
 * The only direct `import.meta.env.VITE_API_URL` read in the whole
 * frontend. Everything else flows through the public-config helper.
 */
export function getApiBaseUrl(): string {
  try {
    const url = (import.meta as ImportMeta & { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
    if (url) return url;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return FALLBACK_API_BASE;
  } catch {
    return FALLBACK_API_BASE;
  }
}

/**
 * Where to redirect after the user signs in. Resolution order:
 *   1. `admin_config.auth.appUrl` (env-correct, operator-tunable)
 *   2. `VITE_FLOW_BUILDER_URL` (build-time override, used by local dev)
 *   3. `https://rynora.app/app` when the marketing site is on rynora.ai
 *      (production split — never fall back to rynora.ai/app)
 *   4. `<same-origin>/app` (QA / UAT / same-host production)
 *   5. `localhost:3100` (last-ditch SSR/test fallback)
 */
export function getFlowBuilderUrl(): string {
  const cfg = getCachedPublicConfigShared();
  if (cfg?.auth.appUrl) return cfg.auth.appUrl;
  try {
    const env = (import.meta as ImportMeta & { env?: { VITE_FLOW_BUILDER_URL?: string } }).env?.VITE_FLOW_BUILDER_URL;
    if (env) return env;
    if (typeof window !== 'undefined' && window.location?.hostname) {
      const host = window.location.hostname;
      if (host === 'rynora.ai' || host === 'www.rynora.ai') {
        return 'https://rynora.app/app';
      }
      if (window.location.origin) {
        return `${window.location.origin}/app`;
      }
    }
    return FALLBACK_FLOW_BUILDER_URL;
  } catch {
    return FALLBACK_FLOW_BUILDER_URL;
  }
}

/**
 * Public status page URL. Surfaced on /status and the footer.
 */
export function getStatusPageUrl(): string {
  try {
    const env = (import.meta as ImportMeta & { env?: { VITE_STATUS_URL?: string } }).env?.VITE_STATUS_URL;
    return env || FALLBACK_STATUS_URL;
  } catch {
    return FALLBACK_STATUS_URL;
  }
}

/**
 * Local dev escape hatch — when truthy, the marketing site renders
 * the "early access" notice instead of executing real OAuth flows.
 * Process-bound on purpose (operators don't toggle this in prod).
 */
export function isAuthDisabled(): boolean {
  try {
    const env = (import.meta as ImportMeta & { env?: { VITE_AUTH_DISABLED?: string } }).env?.VITE_AUTH_DISABLED;
    return !!env && env !== 'false' && env !== '0';
  } catch {
    return false;
  }
}

/**
 * Boot-time public-config load. Wraps the shared helper from
 * @rynora/contracts so call-sites can stay synchronous after this
 * resolves once.
 */
export async function loadPublicConfig(): Promise<PublicConfig> {
  return loadPublicConfigShared({ apiBaseUrl: getApiBaseUrl() });
}

export function getPublicConfig(): PublicConfig | null {
  return getCachedPublicConfigShared();
}
